import { Button, ResponsiveTable, Spinner, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { FleetScheduleRowDto } from './contractsMirror.js';
import { useCompleteReminderMutation, useFleetScheduleQuery } from './useFleetQuery.js';

/**
 * The `/fleet/schedule` tenant-wide schedule page (F21). Lists the next-30-day maintenance
 * reminders + document expirations across every active vehicle, with a per-row "Mark complete"
 * quick-complete (F10) that logs the completing maintenance entry. Accessible table + heading
 * structure (A11Y-01); the SPA never sends a tenant id (SEC-11).
 */

export function ScheduleRoute() {
  const { t } = useTranslation();
  const query = useFleetScheduleQuery();
  const completeMutation = useCompleteReminderMutation();

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('fleet:schedule.errorHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('fleet:schedule.errorBody')}</p>
        <Button asChild variant="secondary">
          <Link to="/fleet">{t('fleet:actions.backToFleet')}</Link>
        </Button>
      </section>
    );
  }

  const rows: FleetScheduleRowDto[] = query.data.rows ?? [];

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('fleet:schedule.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {t('fleet:schedule.subtitle')}
          </p>
        </div>
        <Button asChild variant="secondary">
          <Link to="/fleet">{t('fleet:actions.backToFleet')}</Link>
        </Button>
      </header>

      {rows.length > 0 ? (
        <ResponsiveTable className="rounded-md border border-border bg-card">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">{t('fleet:schedule.caption')}</caption>
            <thead>
              <tr className="border-border border-b text-muted-foreground text-xs uppercase tracking-wide">
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('fleet:schedule.vehicle')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('fleet:schedule.reminder')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('fleet:schedule.status')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('fleet:schedule.documents')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  <span className="sr-only">{t('fleet:schedule.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.vehicleId}-${row.reminder.category}`}
                  className="border-border/60 border-b last:border-0"
                >
                  <td className="px-4 py-3 text-foreground">
                    <Link
                      to="/fleet/$id"
                      params={{ id: String(row.vehicleId) }}
                      className="font-medium text-foreground underline-offset-2 hover:underline"
                    >
                      {row.vehicleDisplayName}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-foreground">
                    <span className="font-medium">{row.reminder.label}</span>
                    <span className="block text-muted-foreground text-xs">
                      {row.reminder.reason}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        row.reminder.isDue
                          ? 'inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive text-xs'
                          : 'inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary-ink text-xs'
                      }
                    >
                      {row.reminder.isDue ? t('fleet:reminder.due') : t('fleet:reminder.upToDate')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {row.expiringDocuments.length > 0
                      ? row.expiringDocuments
                          .map((doc) => `${doc.title} (${doc.expirationDate ?? ''})`)
                          .join(', ')
                      : t('fleet:card.none')}
                  </td>
                  {/* The header for this column is sr-only, so the card view would otherwise
                      print that screen-reader sentence beside the button. */}
                  <td className="px-4 py-3" data-label="">
                    <Button
                      variant="secondary"
                      disabled={completeMutation.isPending}
                      onClick={() =>
                        completeMutation.mutate({
                          vehicleId: row.vehicleId,
                          category: row.reminder.category,
                          odometer: row.currentOdometer,
                        })
                      }
                    >
                      {t('fleet:schedule.markComplete')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResponsiveTable>
      ) : (
        <div className="rounded-md border border-border border-dashed bg-card p-12 text-center">
          <h2 className="font-display font-semibold text-foreground text-lg">
            {t('fleet:schedule.emptyHeading')}
          </h2>
          <p className="mt-1 text-muted-foreground text-sm">{t('fleet:schedule.emptyBody')}</p>
        </div>
      )}
    </div>
  );
}
